<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import CampaignsAPI from 'dashboard/api/campaigns';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  campaignId: {
    type: [Number, String],
    default: null,
  },
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  isWhatsAppType: {
    type: Boolean,
    default: false,
  },
  deliveryStatistics: {
    type: Object,
    default: () => ({}),
  },
  // Owner order 2026-09-29: stop while sending.
  stopUrl: {
    type: String,
    default: '',
  },
  stoppedAt: {
    type: String,
    default: '',
  },
  // Owner order 2026-10-06: paused by the circuit breaker (Meta not
  // answering); a person resumes it from here.
  metaPausedAt: {
    type: String,
    default: '',
  },
  retryFailedUrl: {
    type: String,
    default: '',
  },
  deliveryRecipientsUrl: {
    type: String,
    default: '',
  },
  retrySourceCampaignId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits([
  'edit',
  'delete',
  'retryFailed',
  'viewRecipients',
  'pause',
  'resume',
  'stop',
  'resumeMeta',
]);

const { t } = useI18n();
const downloadingStatus = ref('');

const STATUS_COMPLETED = 'completed';
const STATUS_PROCESSING = 'processing';
const STATUS_ACTIVE = 'active';

// Owner order 2026-09-14 (campaign audit, option 5): a scheduled WhatsApp
// campaign can be edited, paused and resumed until it starts sending.
const isScheduledWhatsApp = computed(
  () => props.isWhatsAppType && props.status === STATUS_ACTIVE
);
const isPaused = computed(() => isScheduledWhatsApp.value && !props.isEnabled);

const { formatMessage } = useMessageFormatter();

const isActive = computed(() =>
  props.isLiveChatType
    ? props.isEnabled
    : props.status !== STATUS_COMPLETED && !isPaused.value
);

const statusTextColor = computed(() => ({
  'text-n-teal-11': isActive.value,
  'text-n-slate-12': !isActive.value,
}));

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (props.status === STATUS_COMPLETED) {
    // A batch that died leaves contacts nobody tried; the card says so
    // instead of a plain "completed" (fork audit 2026-09-24, step 1).
    const unreached = props.deliveryStatistics?.unreached || 0;
    if (props.stoppedAt) {
      return t('CAMPAIGN.WHATSAPP.CARD.STATUS.STOPPED', { count: unreached });
    }
    return unreached
      ? t('CAMPAIGN.WHATSAPP.CARD.STATUS.COMPLETED_WITH_SHORTFALL', {
          count: unreached,
        })
      : t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED');
  }

  if (props.status === STATUS_PROCESSING) {
    return props.metaPausedAt
      ? t('CAMPAIGN.WHATSAPP.CARD.STATUS.META_PAUSED')
      : t('CAMPAIGN.SMS.CARD.STATUS.PROCESSING');
  }

  if (isPaused.value) {
    return t('CAMPAIGN.WHATSAPP.CARD.STATUS.PAUSED');
  }

  return t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const { medium, channel_type: type } = props.inbox || {};
  return getInboxIconByType(type, medium);
});

// Owner order 2026-09-29: while a campaign is still sending, "total" only
// counts what has been created so far, so the card also shows the audience
// size with what is left and an ETA; failures name their top reasons; and
// customers who replied get a tile of their own. Those tiles carry no
// download (there is no recipient status behind them).
const remainingHint = computed(() => {
  const remaining = props.deliveryStatistics.remaining;
  if (remaining === undefined || remaining === null) return '';
  const minutes = Math.max(
    1,
    Math.ceil((props.deliveryStatistics.eta_seconds || 0) / 60)
  );
  return t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.REMAINING', {
    count: remaining,
    minutes,
  });
});

const failureReasonsHint = computed(() => {
  const reasons = props.deliveryStatistics.failure_reasons || [];
  if (!reasons.length) return '';
  const known = new Set([
    '131049',
    '130472',
    '131026',
    '131047',
    '132001',
    '132015',
    '130429',
    '131053',
  ]);
  return reasons
    .map(({ code, count }) => {
      const key = known.has(String(code)) ? String(code) : 'OTHER';
      return `${t(`CAMPAIGN.WHATSAPP.CARD.DELIVERY.REASONS.${key}`)} (${count})`;
    })
    .join(' · ');
});

const deliveryMetrics = computed(() => [
  {
    key: 'all',
    label: t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.TOTAL'),
    value: props.deliveryStatistics.total || 0,
    icon: 'i-lucide-users',
    color: 'text-n-slate-12 bg-n-alpha-2 hover:bg-n-alpha-3',
  },
  ...(props.deliveryStatistics.audience_total !== undefined
    ? [
        {
          key: 'audience',
          label: t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.AUDIENCE'),
          hint: remainingHint.value,
          value: props.deliveryStatistics.audience_total || 0,
          icon: 'i-lucide-target',
          color: 'text-n-slate-12 bg-n-alpha-2',
          noDownload: true,
        },
      ]
    : []),
  {
    key: 'pending',
    label: t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.PENDING'),
    value: props.deliveryStatistics.pending || 0,
    icon: 'i-lucide-clock-3',
    color: 'text-n-amber-11 bg-n-amber-3 hover:bg-n-amber-4',
  },
  {
    key: 'sent',
    label: t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.SENT'),
    value: props.deliveryStatistics.sent || 0,
    icon: 'i-lucide-send',
    color: 'text-n-blue-11 bg-n-blue-3 hover:bg-n-blue-4',
  },
  {
    key: 'delivered',
    label: t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.DELIVERED'),
    // Owner decision (release audit 2026-08-16): delivered includes read,
    // and the UI must say so — the hint below renders as the tile tooltip.
    hint: t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.DELIVERED_INCLUDES_READ'),
    value: props.deliveryStatistics.delivered || 0,
    icon: 'i-lucide-check-check',
    color: 'text-n-teal-11 bg-n-teal-3 hover:bg-n-teal-4',
  },
  {
    key: 'read',
    label: t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.READ'),
    value: props.deliveryStatistics.read || 0,
    icon: 'i-lucide-eye',
    color: 'text-n-teal-11 bg-n-teal-3 hover:bg-n-teal-4',
  },
  {
    key: 'failed',
    label: t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.FAILED'),
    hint: failureReasonsHint.value,
    value: props.deliveryStatistics.failed || 0,
    icon: 'i-lucide-circle-x',
    color: 'text-n-ruby-11 bg-n-ruby-3 hover:bg-n-ruby-4',
  },
  {
    key: 'replied',
    label: t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.REPLIED'),
    hint: t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.REPLIED_HINT'),
    value: props.deliveryStatistics.replied || 0,
    icon: 'i-lucide-message-circle-reply',
    color: 'text-n-iris-11 bg-n-iris-3',
    noDownload: true,
  },
  ...(props.deliveryStatistics.skipped
    ? [
        {
          key: 'skipped',
          label: t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.SKIPPED'),
          hint: t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.SKIPPED_HINT'),
          value: props.deliveryStatistics.skipped,
          icon: 'i-lucide-user-x',
          color: 'text-n-amber-11 bg-n-amber-3',
          noDownload: true,
        },
      ]
    : []),
]);

const downloadRecipients = async metric => {
  if (metric.noDownload) return;
  if (!props.campaignId || downloadingStatus.value) return;

  downloadingStatus.value = metric.key;
  try {
    const response = await CampaignsAPI.downloadRecipients(
      props.campaignId,
      metric.key
    );
    downloadCsvFile(
      `campaign-${props.campaignId}-${metric.key}.csv`,
      response.data
    );
  } catch (error) {
    // A failed export must never fail silently (release-audit C16).
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.DOWNLOAD_ERROR')
    );
  } finally {
    downloadingStatus.value = '';
  }
};
</script>

<template>
  <CardLayout layout="row" class="transition-shadow hover:shadow-md">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-semibold capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="inline-flex h-6 items-center rounded-full bg-n-alpha-2 px-2.5 py-0.5 text-xs font-medium"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
        <span
          v-if="isWhatsAppType && retrySourceCampaignId"
          class="inline-flex h-6 items-center rounded-full bg-n-blue-3 px-2.5 py-0.5 text-xs font-medium text-n-blue-11"
        >
          {{
            t('CAMPAIGN.WHATSAPP.CARD.RETRY_OF', { id: retrySourceCampaignId })
          }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>
      <div
        v-if="isWhatsAppType"
        class="grid w-full grid-cols-2 gap-2 pt-2 sm:grid-cols-3 xl:grid-cols-6"
      >
        <button
          v-for="metric in deliveryMetrics"
          :key="metric.key"
          type="button"
          class="flex min-h-24 flex-col items-start justify-between gap-3 rounded-xl border border-n-weak p-3.5 text-start shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
          :class="metric.color"
          :disabled="
            !metric.noDownload && (!campaignId || Boolean(downloadingStatus))
          "
          :title="
            metric.noDownload
              ? metric.hint || metric.label
              : t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.DOWNLOAD_STATUS', {
                  status: metric.label,
                })
          "
          @click="downloadRecipients(metric)"
        >
          <span class="flex w-full items-center justify-between gap-2">
            <span class="text-xs font-medium">{{ metric.label }}</span>
            <span :class="metric.icon" class="text-base" />
          </span>
          <span v-if="metric.hint" class="text-[10px] leading-tight opacity-80">
            {{ metric.hint }}
          </span>
          <span class="text-2xl font-semibold tabular-nums">
            {{ metric.value }}
          </span>
        </button>
      </div>
    </div>
    <div class="flex flex-wrap items-center justify-end w-36 gap-2">
      <Button
        v-if="isScheduledWhatsApp"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-pencil"
        :title="t('CAMPAIGN.WHATSAPP.CARD.EDIT')"
        @click="emit('edit')"
      />
      <Button
        v-if="isScheduledWhatsApp && !isPaused"
        variant="faded"
        size="sm"
        color="amber"
        icon="i-lucide-pause"
        :title="t('CAMPAIGN.WHATSAPP.CARD.PAUSE')"
        @click="emit('pause')"
      />
      <Button
        v-if="isPaused"
        variant="faded"
        size="sm"
        color="teal"
        icon="i-lucide-play"
        :title="t('CAMPAIGN.WHATSAPP.CARD.RESUME')"
        @click="emit('resume')"
      />
      <Button
        v-if="isWhatsAppType && status === STATUS_PROCESSING && metaPausedAt"
        variant="faded"
        size="sm"
        color="teal"
        icon="i-lucide-play"
        :title="t('CAMPAIGN.WHATSAPP.CARD.RESUME_META')"
        @click="emit('resumeMeta')"
      />
      <Button
        v-if="isWhatsAppType && status === STATUS_PROCESSING && stopUrl"
        variant="faded"
        size="sm"
        color="ruby"
        icon="i-lucide-octagon-x"
        :title="t('CAMPAIGN.WHATSAPP.CARD.STOP')"
        @click="emit('stop')"
      />
      <Button
        v-if="isWhatsAppType && deliveryRecipientsUrl"
        variant="faded"
        color="slate"
        size="sm"
        icon="i-lucide-users"
        :title="t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.VIEW_RECIPIENTS')"
        @click="emit('viewRecipients')"
      />
      <Button
        v-if="
          isWhatsAppType &&
          (deliveryStatistics.failed || deliveryStatistics.unreached) &&
          retryFailedUrl
        "
        variant="faded"
        color="blue"
        size="sm"
        icon="i-lucide-refresh-cw"
        :title="t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.RETRY_FAILED')"
        @click="emit('retryFailed')"
      />
      <Button
        v-if="isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        @click="emit('edit')"
      />
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
